import { AppLauncher } from '../AppLauncher';
import { AppLauncherConfig, FnFallbackCtx } from '../types';
import { formatToArray } from '../util/helper';
export type LauncherCommonConfig
  <T extends Record<string, unknown> = Record<string, unknown>> = Omit<AppLauncherConfig, 'intent' | 'scheme' | 'fallback'>
  & T
  & {
  /**
   * 拉起app失败后的跳转链接，可传入回调函数
   *
   * 如果跳转链接是一个下载地址，则添加前缀DOWNLOAD:
   *
   * DOWNLOAD:http://mcgi.v.qq.com/commdatav2?cmd=4&confid=$\{params.from\}&platform=aphone
   * @param context - 拉起app的信息
   * @param defaultFallback - 腾讯视频的默认下载链接
   */
    fallback?:
    | ((context: FnFallbackCtx, defaultFallback: string) => string)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Exclude<AppLauncherConfig['fallback'], (...args: any[]) => unknown>;
    /**
   * 渠道号，会挂载到model.confid上，如果要使用此confid需要在hook中获取到进行处理
   */
    confid?: string | number;
  };

export type DefaultBehavior = Pick<AppLauncherConfig, 'beforeLaunch' | 'fallback' | 'model' | 'globalParams'>;

export type AppLauncherFactory = (config?: LauncherCommonConfig) => AppLauncher;


/**
 * 通过传入的参数和初始化的参数结合传入给AppLauncher
 * @param cfg - 初始化实例时传入的配置
 * @param defaultBehavior - 最开始给定的默认配置
 */
function createDefaultBehavior(cfg: LauncherCommonConfig, defaultBehavior: DefaultBehavior): DefaultBehavior {
  const { beforeLaunch: defaultBeforeLaunch, fallback: defaultFallback } = defaultBehavior;
  return {
    ...cfg,
    beforeLaunch(context) {
      if (cfg.beforeLaunch && defaultBeforeLaunch) {
        const result = defaultBeforeLaunch(context);
        if (result instanceof Promise) {
          return result.then(context => (cfg.beforeLaunch ? cfg.beforeLaunch(context) : context));
        }
        return cfg.beforeLaunch(result);
      }
      if (cfg.beforeLaunch && !defaultBeforeLaunch) {
        return cfg.beforeLaunch(context);
      }
      if (!cfg.beforeLaunch && defaultBeforeLaunch) {
        return defaultBeforeLaunch(context);
      }
      return context;
    },
    fallback(params) {
      const defaultFallbackUrl = typeof defaultFallback === 'function' ? defaultFallback(params) : defaultFallback ?? '';

      if (typeof cfg.fallback === 'function') {
        return cfg.fallback(params, defaultFallbackUrl);
      }
      return cfg.fallback ?? defaultFallbackUrl;
    },
    globalParams: {
      ...defaultBehavior.globalParams,
      ...cfg.globalParams,
    },
    model: Object.assign(
      {},
      defaultBehavior.model,
      cfg.confid ? { confid: cfg.confid } : {},
      cfg.model,
    ),
  };
}

/**
 * 创建一个 创建AppLauncher实例的工厂函数
 *
 * 此函数被设计为用于为每一个App创建专门的工厂函数
 *
 * @param baseInfo - App的基础信息
 * @returns 用于创建AppLauncher实例的函数
 */
export function createLauncherFactory(baseInfo: AppLauncherConfig): AppLauncherFactory {
  return function createInstance(config: LauncherCommonConfig = {}) {
    return new AppLauncher({
      ...baseInfo,
      ...createDefaultBehavior(config, {
        ...baseInfo,
      }),
      customLaunch: [...formatToArray(config.customLaunch), ...formatToArray(baseInfo.customLaunch)],
    });
  };
}

